/**
 * Правила вывода филиала и канала.
 *
 * Филиала как справочника в 1С нет: в документе стоит лист структуры
 * предприятия («Поставка Траст ЕКБ»), а город назван уровнем выше. Правила
 * на этой странице и превращают одно в другое.
 *
 * Главное здесь — предпросмотр. Правило «всё, где встречается ЕКБ, это
 * Екатеринбург» звучит безобидно ровно до момента, когда под него попадает
 * склад из другого города. Поэтому сначала показывается, что изменится,
 * и только потом человек нажимает «Пересчитать».
 */

import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import type { BranchRule, BranchSummary, RulePreview, WarehouseKindRow } from '../types'
import { Badge, Card, Field, Loading, Notice, TableWrap } from '../components/ui'
import { formatMoney, pluralize } from '../format'

const TARGET_TITLE: Record<string, string> = { branch: 'Филиал', channel: 'Канал и режим', warehouse_kind: 'Вид склада' }

/** Виды складов. Код уходит в правило, человеку — название и что из этого следует. */
const KINDS: Array<{ value: string; title: string; hint: string }> = [
  { value: 'goods', title: 'Товарный', hint: 'входит в запас, оборачиваемость и неликвиды' },
  { value: 'transit', title: 'Товары в пути', hint: 'показывается отдельно от запаса' },
  { value: 'fixed_assets', title: 'Основные средства', hint: 'в запас не входит' },
  { value: 'other', title: 'Прочее нетоварное', hint: 'брак, хозинвентарь, имущество — в запас не входит' },
]
const KIND_TITLE: Record<string, string> = Object.fromEntries(KINDS.map((kind) => [kind.value, kind.title]))

const REASON_TITLE: Record<string, string> = {
  'правило': 'по правилу',
  'договор': 'GUID найден среди договоров, а не складов',
  'по умолчанию': 'ни одно правило не подошло',
}
const APPLIES_TITLE: Record<string, string> = {
  both: 'подразделения и склады',
  department: 'подразделения',
  warehouse: 'склады',
}
const MATCH_TITLE: Record<string, string> = {
  path: 'полный путь по иерархии',
  name: 'собственное наименование',
}

export function AdminRules() {
  const [rules, setRules] = useState<BranchRule[] | null>(null)
  const [branches, setBranches] = useState<BranchSummary[]>([])
  const [warehouses, setWarehouses] = useState<WarehouseKindRow[]>([])
  const [preview, setPreview] = useState<RulePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const [draft, setDraft] = useState({
    target: 'branch',
    applies_to: 'both',
    match_on: 'path',
    priority: 10,
    pattern: '',
    value: '',
    note: '',
  })
  const [newBranch, setNewBranch] = useState({ code: '', name: '' })

  const load = async () => {
    try {
      const [data, next] = await Promise.all([api.branchRules(), api.branchRulesPreview()])
      setRules(data.rules)
      setBranches(data.branches)
      setWarehouses(data.warehouses ?? [])
      setPreview(next)
      setError(null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить правила')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const guard = async (action: () => Promise<void>) => {
    // Флаг, а не только состояние: двойной клик приходит раньше перерисовки.
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await action()
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось выполнить действие')
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  if (error && !rules) return <Notice tone="bad">{error}</Notice>
  if (!rules) return <Loading rows={6} />

  const addRule = () =>
    guard(async () => {
      await api.createBranchRule({
        target: draft.target,
        applies_to: draft.applies_to,
        match_on: draft.match_on,
        priority: Number(draft.priority),
        pattern: draft.pattern.trim(),
        value: draft.value.trim(),
        note: draft.note.trim() || undefined,
      })
      setDraft({ ...draft, pattern: '', value: '', note: '' })
      setNotice('Правило сохранено. Оно ещё не применено — посмотрите предпросмотр и нажмите «Пересчитать».')
    })

  const addBranch = () =>
    guard(async () => {
      await api.createBranch({ code: newBranch.code, name: newBranch.name })
      setNewBranch({ code: '', name: '' })
      setNotice('Филиал заведён. Теперь на него можно сослаться правилом.')
    })

  const apply = () =>
    guard(async () => {
      const result = await api.applyBranchRules()
      setNotice(
        `Пересчитано: ${pluralize(result.departments, 'подразделение', 'подразделения', 'подразделений')}, ` +
          `${pluralize(result.warehouses, 'склад', 'склада', 'складов')}. Витрины обновлены.`,
      )
    })

  const pending = preview?.changed ?? 0

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Notice tone="info">
        Филиала как справочника в 1С нет. В документе стоит лист структуры предприятия — «Поставка Траст
        ЕКБ», — а город назван уровнем выше. Правила ниже выводят филиал из наименований, поэтому новая
        точка или переименование не требуют программиста.
      </Notice>

      {notice && <Notice tone="plain" icon="check">{notice}</Notice>}
      {error && <Notice tone="bad">{error}</Notice>}

      <Card
        flush
        title="Что изменится при пересчёте"
        subtitle={preview ? `Проверено ${pluralize(preview.total, 'узел', 'узла', 'узлов')} справочников. Без филиала: ${preview.without_branch}.` : undefined}
        actions={
          <button className="btn btn--primary" disabled={busy} onClick={() => void apply()}>
            {pending > 0 ? `Пересчитать (${pending})` : 'Пересчитать'}
          </button>
        }
      >
        {!preview || preview.rows.length === 0 ? (
          <p className="muted panel__pad">
            Всё уже посчитано по текущим правилам, и ни один узел не остался без филиала.
          </p>
        ) : (
          <TableWrap>
            <table className="data">
              <thead>
                <tr>
                  <th>Узел</th>
                  <th>Путь по иерархии</th>
                  <th>Сейчас</th>
                  <th>Станет</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={`${row.kind}:${row.guid}`}>
                    <td>
                      <div>{row.name}</div>
                      <div className="tiny muted">
                        {row.kind === 'department' ? 'подразделение' : 'склад'}
                        {row.next_channel ? ` · ${row.next_channel}` : ''}
                      </div>
                    </td>
                    <td className="tiny muted">{row.path}</td>
                    <td>
                      {row.current_branch}
                      {row.current_kind && <div className="tiny muted">{row.current_kind}</div>}
                    </td>
                    <td>
                      {row.changed ? (
                        <Badge tone={row.next_branch === 'Не определено' ? 'bad' : 'good'}>
                          {row.next_branch}
                        </Badge>
                      ) : (
                        <Badge tone="bad">{row.next_branch}</Badge>
                      )}
                      {row.next_kind && row.next_kind !== row.current_kind && (
                        <div className="tiny" style={{ marginTop: 4 }}>
                          <Badge tone="good">{row.next_kind}</Badge>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card flush title="Правила" subtitle="Побеждает правило с наименьшим приоритетом">
        <TableWrap>
          <table className="data">
            <thead>
              <tr>
                <th>Выводит</th>
                <th>Значение</th>
                <th>Приоритет</th>
                <th>Применяется к</th>
                <th>Сравнивается с</th>
                <th>Выражение</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>{TARGET_TITLE[rule.target] ?? rule.target}</td>
                  <td>
                    <div>{rule.target === 'warehouse_kind' ? (KIND_TITLE[rule.value] ?? rule.value) : rule.value}</div>
                    {rule.note && <div className="tiny muted">{rule.note}</div>}
                  </td>
                  <td>{rule.priority}</td>
                  <td className="tiny">{APPLIES_TITLE[rule.applies_to] ?? rule.applies_to}</td>
                  <td className="tiny">{MATCH_TITLE[rule.match_on] ?? rule.match_on}</td>
                  <td>
                    <code className="tiny">{rule.pattern}</code>
                  </td>
                  <td>
                    <button
                      className="btn btn--sm btn--ghost btn--danger"
                      disabled={busy}
                      onClick={() => void guard(async () => void (await api.deleteBranchRule(rule.id)))}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <Card title="Новое правило">
        <div className="grid grid--2">
          <Field label="Что выводим">
            <select
              className="select"
              value={draft.target}
              onChange={(e) => {
                const target = e.target.value
                // Вид склада бывает только у склада и ищется по его имени.
                setDraft(target === 'warehouse_kind'
                  ? { ...draft, target, value: '', applies_to: 'warehouse', match_on: 'name' }
                  : { ...draft, target, value: '' })
              }}
            >
              <option value="branch">Филиал</option>
              <option value="channel">Канал и режим</option>
              <option value="warehouse_kind">Вид склада</option>
            </select>
          </Field>

          <Field
            label="Значение"
            hint={
              draft.target === 'branch'
                ? 'Код филиала из списка ниже'
                : draft.target === 'warehouse_kind'
                  ? KINDS.find((kind) => kind.value === draft.value)?.hint ?? 'Что считать товарным запасом'
                  : 'Например: Розница'
            }
          >
            {draft.target === 'warehouse_kind' ? (
              <select className="select" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })}>
                <option value="">— выберите —</option>
                {KINDS.map((kind) => (
                  <option key={kind.value} value={kind.value}>
                    {kind.title}
                  </option>
                ))}
              </select>
            ) : draft.target === 'branch' ? (
              <select className="select" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })}>
                <option value="">— выберите —</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.code}>
                    {branch.name} ({branch.code})
                  </option>
                ))}
              </select>
            ) : (
              <input className="input" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
            )}
          </Field>

          <Field label="Применяется к">
            <select
              className="select"
              value={draft.applies_to}
              disabled={draft.target === 'warehouse_kind'}
              onChange={(e) => setDraft({ ...draft, applies_to: e.target.value })}
            >
              <option value="both">Подразделения и склады</option>
              <option value="department">Только подразделения</option>
              <option value="warehouse">Только склады</option>
            </select>
          </Field>

          <Field label="Сравнивать с" hint="Путь — для филиала, наименование — для канала">
            <select className="select" value={draft.match_on} onChange={(e) => setDraft({ ...draft, match_on: e.target.value })}>
              <option value="path">Полный путь по иерархии</option>
              <option value="name">Собственное наименование</option>
            </select>
          </Field>

          <Field label="Приоритет" hint="Меньше — раньше">
            <input className="input"
              type="number"
              value={draft.priority}
              onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })}
            />
          </Field>

          <Field label="Выражение" hint="Регулярное выражение, регистр не важен">
            <input className="input"
              value={draft.pattern}
              placeholder="пермь|(?<![а-яёa-z])прм(?![а-яёa-z])"
              onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
            />
          </Field>

          <Field label="Комментарий">
            <input className="input" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          </Field>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <button
            className="btn btn--primary"
            disabled={busy || !draft.pattern.trim() || !draft.value.trim()}
            onClick={() => void addRule()}
          >
            Сохранить правило
          </button>
          <span className="tiny muted">
            Правило сохраняется сразу, но на отчёты влияет только после пересчёта.
          </span>
        </div>
      </Card>

      <Card
        flush
        title="Виды складов"
        subtitle="Товарный запас, оборачиваемость и неликвиды считаются только по товарным складам. Товары в пути — отдельно. Вид меняется правилом «Вид склада» выше и пересчётом"
      >
        {warehouses.length === 0 ? (
          <p className="muted panel__pad">Складов ещё нет: они появятся с первым пакетом остатков или справочников.</p>
        ) : (
          <TableWrap>
            <table className="data">
              <thead>
                <tr>
                  <th>Склад</th>
                  <th>Вид</th>
                  <th>Почему</th>
                  <th className="num">Остаток, ₽</th>
                </tr>
              </thead>
              <tbody>
                {warehouses.map((row) => (
                  <tr key={row.guid}>
                    <td>
                      <div>{row.name}</div>
                      {row.path !== row.name && <div className="tiny muted">{row.path}</div>}
                    </td>
                    <td>{row.kind === 'goods' ? row.kind_title : <Badge tone={row.kind === 'transit' ? 'warn' : undefined}>{row.kind_title}</Badge>}</td>
                    <td className="tiny muted">{REASON_TITLE[row.reason] ?? row.reason}</td>
                    <td className="num mono">{row.amount ? formatMoney(row.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card flush title="Филиалы" subtitle="Синтетическое измерение: в 1С такого справочника нет">
        <TableWrap>
          <table className="data">
            <thead>
              <tr>
                <th>Филиал</th>
                <th>Код</th>
                <th className="num">Подразделений</th>
                <th className="num">Складов</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((branch) => (
                <tr key={branch.id}>
                  <td>{branch.name}</td>
                  <td>
                    <code className="tiny">{branch.code}</code>
                  </td>
                  <td className="num">{branch.departments}</td>
                  <td className="num">{branch.warehouses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>

        <div className="form-inline panel__pad">
          <Field label="Код" hint="Латиница, 2–16 символов">
            <input className="input"
              value={newBranch.code}
              placeholder="PRM"
              onChange={(e) => setNewBranch({ ...newBranch, code: e.target.value })}
            />
          </Field>
          <Field label="Наименование">
            <input className="input"
              value={newBranch.name}
              placeholder="Пермь"
              onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
            />
          </Field>
          <div className="field form-inline__action">
            <span className="field__label" aria-hidden="true">&nbsp;</span>
          <button
            className="btn"
            disabled={busy || !newBranch.code.trim() || !newBranch.name.trim()}
            onClick={() => void addBranch()}
          >
            Добавить филиал
          </button>
          </div>
        </div>
      </Card>
    </div>
  )
}
